/**
 * StudyList Page
 * Main dashboard with study cards, filters, and tabs
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, Plus, ExternalLink, Filter, Settings, ChevronDown, User, Building2, Archive } from 'lucide-react';
import { useStudyOrganizer } from '../contexts';
import { StudyCard } from '../components';
import { ENROLLMENT_STATUSES, type EnrollmentStatus } from '../types';
import api from '../../services/api';

interface Site {
  id: string;
  name: string;
  code: string;
}

interface QuickLink {
  id: string;
  type: 'site' | 'personal';
  name: string;
  url: string;
  description: string | null;
  icon_url: string | null;
}

export function StudyList() {
  const {
    filteredProtocols,
    loading,
    error,
    filters,
    setSearch,
    setSiteFilter,
    setStatusFilter,
    setShowStarredOnly,
    setShowArchived,
    clearFilters,
    isStarred,
    toggleStar,
    myStudiesCount,
    allStudiesCount,
    fetchProtocols,
  } = useStudyOrganizer();

  const [sites, setSites] = useState<Site[]>([]);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [quickLinks, setQuickLinks] = useState<QuickLink[]>([]);
  const [showLinksDropdown, setShowLinksDropdown] = useState(false);

  // Fetch user's sites for the filter dropdown
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await api.get('/api/sites');
        setSites(response.data);
      } catch (err) {
        console.error('Error fetching sites:', err);
      }
    };
    fetchSites();
  }, []);

  // Fetch quick links (org + personal) when site filter changes or on load
  useEffect(() => {
    const fetchQuickLinks = async () => {
      // Use filtered site or first available site
      const siteId = filters.siteId || (sites.length === 1 ? sites[0].id : null);
      if (!siteId) {
        setQuickLinks([]);
        return;
      }
      try {
        const response = await api.get('/api/my-quick-links', {
          headers: { 'X-Site-Id': siteId }
        });
        setQuickLinks(response.data.links || []);
      } catch (err) {
        console.error('Error fetching quick links:', err);
      }
    };
    fetchQuickLinks();
  }, [filters.siteId, sites]);

  const hasActiveFilters = filters.search || filters.siteId || filters.statuses.length > 0;

  const handleStatusToggle = (status: EnrollmentStatus) => {
    const newStatuses = filters.statuses.includes(status)
      ? filters.statuses.filter(s => s !== status)
      : [...filters.statuses, status];
    setStatusFilter(newStatuses);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="py-4 flex items-center gap-2 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[160px] max-w-md">
              <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search protocols, sponsors, indications..."
                className="w-full pl-8 pr-8 py-1.5 min-h-[44px] sm:min-h-0 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
              />
              {filters.search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Site filter */}
            <select
              value={filters.siteId || ''}
              onChange={e => setSiteFilter(e.target.value || null)}
              className="px-2.5 py-1.5 min-h-[44px] sm:min-h-0 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            >
              <option value="">All Sites</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>

            {/* Status filter */}
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 ${
                  filters.statuses.length > 0
                    ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <Filter size={16} />
                Status
                {filters.statuses.length > 0 && (
                  <span className="bg-primary-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {filters.statuses.length}
                  </span>
                )}
              </button>
              {showStatusDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowStatusDropdown(false)} />
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-2 dark:bg-gray-800 dark:border-gray-700">
                    {ENROLLMENT_STATUSES.map(status => (
                      <label
                        key={status.value}
                        className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.statuses.includes(status.value)}
                          onChange={() => handleStatusToggle(status.value)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-200">{status.label}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Clear filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-2.5 py-1.5 min-h-[44px] sm:min-h-0 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Clear Filters
              </button>
            )}

            {/* Show Archived toggle */}
            <button
              onClick={() => setShowArchived(!filters.showArchived)}
              className={`inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 sm:py-1.5 ${
                filters.showArchived
                  ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <Archive size={14} />
              <span className="hidden sm:inline">Show Archived</span>
            </button>

            <div className="flex-1 hidden sm:block" />

            {/* Quick Links Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowLinksDropdown(!showLinksDropdown)}
                className="px-2.5 py-1.5 min-h-[44px] sm:min-h-0 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center gap-1"
              >
                Links
                <ChevronDown size={14} />
              </button>
              {showLinksDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowLinksDropdown(false)} />
                  <div className="absolute right-0 top-full mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-2 max-h-96 overflow-y-auto dark:bg-gray-800 dark:border-gray-700">
                    {!(filters.siteId || sites.length === 1) ? (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        Select a site to view links
                      </div>
                    ) : quickLinks.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        No links configured
                      </div>
                    ) : (
                      <>
                        {/* My Links */}
                        {quickLinks.filter(l => l.type === 'personal').length > 0 && (
                          <div className="mb-2">
                            <div className="px-4 py-1 flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              <User size={12} />
                              My Links
                            </div>
                            {quickLinks.filter(l => l.type === 'personal').map(link => (
                              <a
                                key={link.id}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setShowLinksDropdown(false)}
                                className="flex items-center gap-3 px-4 py-2 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                              >
                                {link.icon_url ? (
                                  <img src={link.icon_url} alt="" className="w-4 h-4" />
                                ) : (
                                  <ExternalLink size={14} className="text-primary-400" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{link.name}</div>
                                  {link.description && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{link.description}</div>
                                  )}
                                </div>
                              </a>
                            ))}
                          </div>
                        )}

                        {/* Org Links */}
                        {quickLinks.filter(l => l.type === 'site').length > 0 && (
                          <div className={quickLinks.filter(l => l.type === 'personal').length > 0 ? 'border-t border-gray-100 dark:border-gray-700 pt-2' : ''}>
                            <div className="px-4 py-1 flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              <Building2 size={12} />
                              Organization
                            </div>
                            {quickLinks.filter(l => l.type === 'site').map(link => (
                              <a
                                key={link.id}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setShowLinksDropdown(false)}
                                className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                {link.icon_url ? (
                                  <img src={link.icon_url} alt="" className="w-4 h-4" />
                                ) : (
                                  <ExternalLink size={14} className="text-gray-400 dark:text-gray-500" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{link.name}</div>
                                  {link.description && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{link.description}</div>
                                  )}
                                </div>
                              </a>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    <div className="border-t border-gray-100 dark:border-gray-700 mt-2 pt-2">
                      <Link
                        to="/siteorganizer/org-links"
                        onClick={() => setShowLinksDropdown(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                      >
                        <Settings size={14} />
                        Manage Links
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </div>

            <Link
              to="/siteorganizer/add"
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">Add Study</span>
            </Link>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700 -mb-px">
            <button
              onClick={() => setShowStarredOnly(true)}
              className={`min-h-[44px] whitespace-nowrap border-b-2 px-2.5 text-sm font-medium transition-colors duration-150 sm:min-h-0 sm:py-2 ${
                filters.showStarredOnly
                  ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              My Studies ({myStudiesCount})
            </button>
            <button
              onClick={() => setShowStarredOnly(false)}
              className={`min-h-[44px] whitespace-nowrap border-b-2 px-2.5 text-sm font-medium transition-colors duration-150 sm:min-h-0 sm:py-2 ${
                !filters.showStarredOnly
                  ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              All Studies ({allStudiesCount})
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-32 animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={() => fetchProtocols()}
              className="mt-4 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Try Again
            </button>
          </div>
        ) : filteredProtocols.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">📋</div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {filters.showStarredOnly ? 'No starred studies' : 'No studies found'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {filters.showStarredOnly
                ? 'Star studies to add them to your personal list'
                : hasActiveFilters
                ? 'Try adjusting your filters'
                : 'Get started by adding your first study'}
            </p>
            {!filters.showStarredOnly && !hasActiveFilters && (
              <Link
                to="/siteorganizer/add"
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
              >
                <Plus size={16} />
                Add Study
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProtocols.map(protocol => (
              <StudyCard
                key={protocol.id}
                protocol={protocol}
                bookmarks={protocol.bookmarks}
                isStarred={isStarred(protocol.id)}
                onToggleStar={() => toggleStar(protocol.id)}
              />
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

export default StudyList;
